import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    FormControl,
    Select,
    MenuItem,
    Slider,
    Typography,
    Button,
    Card,
    CardContent,
    Chip,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    Checkbox,
    FormGroup,
    TextField,
    LinearProgress,
    Alert,
    Divider,
    Box,
    Menu,
    ListItemText,
    Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import MapIcon from '@mui/icons-material/Map';
import ClearIcon from '@mui/icons-material/Clear';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SortIcon from '@mui/icons-material/Sort';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatNumber } from 'util/Funcs';
import { catastroService } from 'services/catastroService';
import { CatastroSearchParams, ParcelResultDto, MunicipalityDto, BuildingDetail, UnitDetail } from 'types/Catastro';
import RangeFilter from 'components/Filter/RangeFilter';
import YearRangeFilter from 'components/Filter/YearRangeFilter';
import ClearFilterButton from 'components/Filter/ClearFilterButton';
import useUserStore from 'store/useUserStore';
import { Navigate } from 'react-router-dom';

const USAGE_TYPES = ['residential', 'commercial', 'industrial', 'agricultural', 'mixed', 'other'];

const CatastroSearchPage: React.FC = () => {
    const { isProUser } = useUserStore();

    // Redirect non-Pro users to the main page
    if (!isProUser()) {
        return <Navigate to="/main" replace />;
    }

    const [searchParams, setSearchParams] = useState<CatastroSearchParams>({
        maxResults: 20,
        usageTypes: [],
    });
    const [hasSearched, setHasSearched] = useState(false);
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [filterStates, setFilterStates] = useState({
        parcelAreaEnabled: true,
        builtAreaEnabled: true,
        livingAreaEnabled: true,
        buildingCountEnabled: true,
    });

    // Load municipalities
    const { data: municipalities } = useQuery({
        queryKey: ['catastro-municipalities'],
        queryFn: () => catastroService.getMunicipalities(),
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    // Search parcels
    const {
        data: searchResults,
        isFetching: isSearching,
        error: searchError,
    } = useQuery({
        queryKey: ['catastro-search', searchParams],
        queryFn: () => catastroService.searchParcels(searchParams),
        enabled: hasSearched,
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchOnWindowFocus: false,
    });

    const handleParamChange = useCallback((key: keyof CatastroSearchParams, value: any) => {
        setSearchParams((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleParcelAreaChange = useCallback(
        (range: [number, number]) => {
            if (filterStates.parcelAreaEnabled) {
                setSearchParams((prev) => ({
                    ...prev,
                    parcelMin: range[0],
                    parcelMax: range[1],
                }));
            }
        },
        [filterStates.parcelAreaEnabled],
    );

    const handleBuiltAreaChange = useCallback(
        (range: [number, number]) => {
            if (filterStates.builtAreaEnabled) {
                setSearchParams((prev) => ({
                    ...prev,
                    builtMin: range[0],
                    builtMax: range[1],
                }));
            }
        },
        [filterStates.builtAreaEnabled],
    );

    const handleLivingAreaChange = useCallback(
        (range: [number, number]) => {
            if (filterStates.livingAreaEnabled) {
                setSearchParams((prev) => ({
                    ...prev,
                    floorAreaMin: range[0],
                    floorAreaMax: range[1],
                }));
            }
        },
        [filterStates.livingAreaEnabled],
    );

    const handleBuildingCountChange = useCallback(
        (range: [number, number]) => {
            if (filterStates.buildingCountEnabled) {
                setSearchParams((prev) => ({
                    ...prev,
                    buildingMin: range[0],
                    buildingMax: range[1],
                }));
            }
        },
        [filterStates.buildingCountEnabled],
    );

    const handleYearRangeChange = useCallback((range: [number | undefined, number | undefined]) => {
        setSearchParams((prev) => ({
            ...prev,
            yearMin: range[0],
            yearMax: range[1],
        }));
    }, []);

    const handleSearch = useCallback(() => {
        setHasSearched(true);
    }, []);

    const handleClear = useCallback(() => {
        setSearchParams({
            maxResults: 20,
            usageTypes: [],
        });
        setFilterStates({
            parcelAreaEnabled: true,
            builtAreaEnabled: true,
            livingAreaEnabled: true,
            buildingCountEnabled: true,
        });
        setHasSearched(false);
    }, []);

    const handleFilterToggle = useCallback((filterType: keyof typeof filterStates) => {
        setFilterStates((prev) => {
            const newState = {
                ...prev,
                [filterType]: !prev[filterType],
            };

            // When enabling a filter, remove it from search params (no filter applied)
            if (!prev[filterType]) {
                switch (filterType) {
                    case 'parcelAreaEnabled':
                        setSearchParams((searchPrev) => ({
                            ...searchPrev,
                            parcelMin: undefined,
                            parcelMax: undefined,
                        }));
                        break;
                    case 'builtAreaEnabled':
                        setSearchParams((searchPrev) => ({
                            ...searchPrev,
                            builtMin: undefined,
                            builtMax: undefined,
                        }));
                        break;
                    case 'livingAreaEnabled':
                        setSearchParams((searchPrev) => ({
                            ...searchPrev,
                            floorAreaMin: undefined,
                            floorAreaMax: undefined,
                        }));
                        break;
                }
            } else {
                // When disabling a filter, set it to 0-0 range
                switch (filterType) {
                    case 'parcelAreaEnabled':
                        setSearchParams((searchPrev) => ({
                            ...searchPrev,
                            parcelMin: 0,
                            parcelMax: 0,
                        }));
                        break;
                    case 'builtAreaEnabled':
                        setSearchParams((searchPrev) => ({
                            ...searchPrev,
                            builtMin: 0,
                            builtMax: 0,
                        }));
                        break;
                    case 'livingAreaEnabled':
                        setSearchParams((searchPrev) => ({
                            ...searchPrev,
                            floorAreaMin: 0,
                            floorAreaMax: 0,
                        }));
                        break;
                    case 'buildingCountEnabled':
                        setSearchParams((searchPrev) => ({
                            ...searchPrev,
                            buildingMin: 0,
                            buildingMax: 0,
                        }));
                        break;
                }
            }

            return newState;
        });
    }, []);

    const toggleExpanded = useCallback((referenciaCatastral: string, event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setExpandedItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(referenciaCatastral)) {
                newSet.delete(referenciaCatastral);
            } else {
                newSet.add(referenciaCatastral);
            }
            return newSet;
        });
    }, []);

    const handleViewDetails = useCallback((referenciaCatastral: string, event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const url = `https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCBusqueda.aspx?fromVolver=ListaBienes&tipoVia=&via=&num=&blq=&esc=&plt=&pta=&descProv=&prov=&mun=&descMuni=&TipUR=&codVia=&comVia=&final=&pest=rc&pol=&par=&Idufir=&RCCompleta=${referenciaCatastral}&latitud=&longitud=&gradoslat=&minlat=&seglat=&gradoslon=&minlon=&seglon=&x=&y=&huso=&tipoCoordenadas=`;

        window.open(url, '_blank');
    }, []);

    const handleItemClick = useCallback(
        (referenciaCatastral: string, event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();

            // Find the parcel data for logging
            const parcelData = searchResults?.find((parcel) => parcel.referencia_catastral === referenciaCatastral);
            if (parcelData) {
                console.log('Parcel item opened:', parcelData);
            }

            toggleExpanded(referenciaCatastral, event);
        },
        [toggleExpanded, searchResults],
    );

    const hasFilters = Object.keys(searchParams).some((key) => {
        const value = searchParams[key as keyof CatastroSearchParams];
        if (key === 'maxResults') return false;
        return value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0);
    });

    // Custom Dropdown Component to match RangeFilter styling
    const CustomDropdown = ({
        title,
        value,
        options,
        onChange,
        placeholder,
        multiple = false,
        renderValue,
        searchable = false,
    }: {
        title: string;
        value: string | string[];
        options: { value: string; label: string }[];
        onChange: (value: string | string[]) => void;
        placeholder: string;
        multiple?: boolean;
        renderValue?: (value: string | string[]) => string;
        searchable?: boolean;
    }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const [searchTerm, setSearchTerm] = useState('');
        const open = Boolean(anchorEl);

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
            setSearchTerm('');
        };

        const handleSelectItem = (selectedValue: string) => {
            if (multiple) {
                const currentValues = Array.isArray(value) ? value : [];
                const newValues = currentValues.includes(selectedValue) ? currentValues.filter((v) => v !== selectedValue) : [...currentValues, selectedValue];
                onChange(newValues);
            } else {
                onChange(selectedValue);
                handleClose();
            }
        };

        const displayValue = () => {
            if (renderValue) {
                return renderValue(value);
            }
            if (multiple && Array.isArray(value)) {
                return value.length > 0 ? value.join(', ') : placeholder;
            }
            return value || placeholder;
        };

        const isEmpty = multiple ? !Array.isArray(value) || value.length === 0 : !value;

        // Filter options based on search term
        const filteredOptions = searchable ? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())) : options;

        return (
            <div className="relative w-full h-full border border-my-gray rounded-[5px]">
                <div className="flex items-center justify-between gap-[15px] w-full h-full px-[10px] cursor-pointer" onClick={handleClick}>
                    <div className={`truncate ${isEmpty ? 'text-gray-500' : ''}`}>{displayValue()}</div>
                    {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </div>
                {!open && !isEmpty && <ClearFilterButton onClicked={() => onChange(multiple ? [] : '')} />}
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose} sx={{ marginTop: '10px', maxHeight: '300px' }}>
                    {searchable && (
                        <div className="px-3 py-2 border-b border-gray-200">
                            <TextField
                                size="small"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '32px',
                                        fontSize: '14px',
                                    },
                                }}
                            />
                        </div>
                    )}
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <MenuItem key={option.value} onClick={() => handleSelectItem(option.value)} sx={{ minWidth: '200px' }}>
                                    {multiple && <Checkbox checked={Array.isArray(value) && value.includes(option.value)} sx={{ marginRight: 1 }} />}
                                    <ListItemText primary={option.label} />
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled>
                                <ListItemText primary="No results found" />
                            </MenuItem>
                        )}
                    </div>
                </Menu>
            </div>
        );
    };

    return (
        <div className="container mx-auto px-6 py-8">
            {/* Main Search Panel */}
            <div className="bg-white p-8 rounded-2xl shadow-md">
                {/* Header */}
                <div className="flex items-start mb-6">
                    <div className="bg-my-blue bg-opacity-10 p-3 rounded-full mr-6">
                        <SearchIcon className="text-my-blue text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Catastro Search</h1>
                        <p className="text-gray-600 mt-1">
                            Search and analyze cadastral parcels with advanced filtering, ranking, and geographical information. Find properties by region,
                            area, construction year, usage type, and more.
                        </p>
                    </div>
                </div>

                {/* Search Filters Panel */}
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                    {/* Filter Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4 mb-6">
                        {/* Municipality Filter */}
                        <div className="relative col-span-1 md:col-span-1 xl:col-span-1">
                            <div className="h-[20px] mb-1">
                                <label className="text-sm font-medium text-gray-700">Municipality</label>
                            </div>
                            <div className="h-[42px]">
                                <CustomDropdown
                                    title="Municipality"
                                    value={searchParams.region || ''}
                                    options={
                                        municipalities?.map((municipality) => ({
                                            value: municipality.municipio,
                                            label: municipality.municipio,
                                        })) || []
                                    }
                                    onChange={(value) => handleParamChange('region', value as string)}
                                    placeholder="Select Municipality"
                                    searchable={true}
                                />
                            </div>
                        </div>

                        {/* Parcel Area Filter */}
                        <div className="relative col-span-1 md:col-span-1 xl:col-span-1">
                            <div className="h-[20px] mb-1 flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Parcel Area</label>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">{filterStates.parcelAreaEnabled ? 'Enabled' : 'Empty'}</span>
                                    <Switch
                                        checked={filterStates.parcelAreaEnabled}
                                        onChange={() => handleFilterToggle('parcelAreaEnabled')}
                                        size="small"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#3176BC',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#3176BC',
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={`h-[42px] ${!filterStates.parcelAreaEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                                <RangeFilter
                                    title="Parcel Area"
                                    limit={[0, 100000]}
                                    unit="m²"
                                    value={filterStates.parcelAreaEnabled ? [searchParams.parcelMin || 0, searchParams.parcelMax || 0] : [0, 0]}
                                    onChanged={handleParcelAreaChange}
                                    steps={[
                                        { max: 1000, step: 50 },
                                        { max: 5000, step: 200 },
                                        { max: 20000, step: 1000 },
                                        { max: 100000, step: 5000 },
                                    ]}
                                    data-cy="parcel-area-slider"
                                    disabled={!filterStates.parcelAreaEnabled}
                                    disabledText="0"
                                />
                            </div>
                        </div>

                        {/* Built Area Filter */}
                        <div className="relative col-span-1 md:col-span-1 xl:col-span-1">
                            <div className="h-[20px] mb-1 flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Built Area</label>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">{filterStates.builtAreaEnabled ? 'Enabled' : 'Empty'}</span>
                                    <Switch
                                        checked={filterStates.builtAreaEnabled}
                                        onChange={() => handleFilterToggle('builtAreaEnabled')}
                                        size="small"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#3176BC',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#3176BC',
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={`h-[42px] ${!filterStates.builtAreaEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                                <RangeFilter
                                    title="Built Area"
                                    limit={[0, 10000]}
                                    unit="m²"
                                    value={filterStates.builtAreaEnabled ? [searchParams.builtMin || 0, searchParams.builtMax || 0] : [0, 0]}
                                    onChanged={handleBuiltAreaChange}
                                    steps={[
                                        { max: 500, step: 25 },
                                        { max: 2000, step: 100 },
                                        { max: 10000, step: 500 },
                                    ]}
                                    data-cy="built-area-slider"
                                    disabled={!filterStates.builtAreaEnabled}
                                    disabledText="0"
                                />
                            </div>
                        </div>

                        {/* Living Area Filter */}
                        <div className="relative col-span-1 md:col-span-1 xl:col-span-1">
                            <div className="h-[20px] mb-1 flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Living Area</label>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">{filterStates.livingAreaEnabled ? 'Enabled' : 'Empty'}</span>
                                    <Switch
                                        checked={filterStates.livingAreaEnabled}
                                        onChange={() => handleFilterToggle('livingAreaEnabled')}
                                        size="small"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#3176BC',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#3176BC',
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={`h-[42px] ${!filterStates.livingAreaEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                                <RangeFilter
                                    title="Living Area"
                                    limit={[0, 15000]}
                                    unit="m²"
                                    value={filterStates.livingAreaEnabled ? [searchParams.floorAreaMin || 0, searchParams.floorAreaMax || 0] : [0, 0]}
                                    onChanged={handleLivingAreaChange}
                                    steps={[
                                        { max: 500, step: 25 },
                                        { max: 2000, step: 100 },
                                        { max: 5000, step: 250 },
                                        { max: 15000, step: 500 },
                                    ]}
                                    data-cy="living-area-slider"
                                    disabled={!filterStates.livingAreaEnabled}
                                    disabledText="0"
                                />
                            </div>
                        </div>

                        {/* Construction Year Filter */}
                        <div className="relative col-span-1 md:col-span-1 xl:col-span-1">
                            <div className="h-[20px] mb-1">
                                <label className="text-sm font-medium text-gray-700">Construction Year</label>
                            </div>
                            <div className="h-[42px]">
                                <YearRangeFilter
                                    title="Construction Year"
                                    value={[searchParams.yearMin, searchParams.yearMax]}
                                    onChanged={handleYearRangeChange}
                                    minYear={1800}
                                    maxYear={2024}
                                    steps={[
                                        { max: 1900, step: 50 },
                                        { max: 1970, step: 10 },
                                        { max: 2020, step: 5 },
                                        { max: 2024, step: 1 },
                                    ]}
                                    data-cy="construction-year"
                                />
                            </div>
                        </div>

                        {/* Usage Types Filter */}
                        <div className="relative col-span-1 md:col-span-1 xl:col-span-1">
                            <div className="h-[20px] mb-1">
                                <label className="text-sm font-medium text-gray-700">Usage Types</label>
                            </div>
                            <div className="h-[42px]">
                                <CustomDropdown
                                    title="Usage Types"
                                    value={searchParams.usageTypes || []}
                                    options={USAGE_TYPES.map((type) => ({
                                        value: type,
                                        label: type.charAt(0).toUpperCase() + type.slice(1),
                                    }))}
                                    onChange={(value) => handleParamChange('usageTypes', value as string[])}
                                    placeholder="Select Type"
                                    multiple={true}
                                />
                            </div>
                        </div>

                        {/* Building Count Filter */}
                        <div className="relative col-span-1 md:col-span-1 xl:col-span-1">
                            <div className="h-[20px] mb-1 flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Buildings</label>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">{filterStates.buildingCountEnabled ? 'Enabled' : 'Empty'}</span>
                                    <Switch
                                        checked={filterStates.buildingCountEnabled}
                                        onChange={() => handleFilterToggle('buildingCountEnabled')}
                                        size="small"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#3176BC',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#3176BC',
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={`h-[42px] ${!filterStates.buildingCountEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                                <RangeFilter
                                    title="Buildings"
                                    limit={[0, 50]}
                                    unit=" buildings"
                                    value={filterStates.buildingCountEnabled ? [searchParams.buildingMin || 0, searchParams.buildingMax || 0] : [0, 0]}
                                    onChanged={handleBuildingCountChange}
                                    steps={[
                                        { max: 10, step: 1 },
                                        { max: 50, step: 5 },
                                    ]}
                                    data-cy="building-count-slider"
                                    disabled={!filterStates.buildingCountEnabled}
                                    disabledText="0"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bottom Controls */}
                    <div className="flex justify-between items-center">
                        {/* Max Results */}
                        <div>
                            <label className="text-sm font-medium text-gray-700 block mb-1" htmlFor="max-results">
                                Max Results
                            </label>
                            <TextField
                                id="max-results"
                                type="number"
                                value={searchParams.maxResults || 20}
                                onChange={(e) => handleParamChange('maxResults', Number(e.target.value))}
                                className="w-24"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '42px',
                                        borderRadius: '8px',
                                        border: '1px solid #D1D5DB',
                                        '&:hover': {
                                            borderColor: '#3176BC',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: '#3176BC',
                                            boxShadow: '0 0 0 1px #3176BC',
                                        },
                                    },
                                }}
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                            {/* Clear Button */}
                            {hasFilters && (
                                <button
                                    onClick={handleClear}
                                    className="bg-red-100 text-red-600 font-medium py-2 px-3 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center h-[42px]"
                                >
                                    <ClearIcon className="mr-1 text-sm" />
                                    Clear
                                </button>
                            )}

                            {/* Search Button */}
                            <button
                                onClick={handleSearch}
                                disabled={isSearching}
                                className="bg-my-blue text-white font-medium py-2 px-4 rounded-lg shadow-sm hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-my-blue flex items-center disabled:opacity-50 h-[42px]"
                            >
                                <SearchIcon className="mr-2 text-sm" />
                                {isSearching ? 'Searching...' : 'Search Parcels'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Results */}
            {hasSearched && (
                <div className="mt-8">
                    {isSearching && (
                        <div className="flex items-center justify-center py-8">
                            <LinearProgress className="w-full max-w-md" />
                        </div>
                    )}

                    {searchError && (
                        <Alert severity="error" className="mb-4">
                            Error searching parcels: {searchError.message}
                        </Alert>
                    )}

                    {!isSearching && searchResults && (
                        <>
                            {/* Results Header */}
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-gray-800">Found {searchResults.length} Parcels</h2>
                                <div className="flex items-center text-sm text-gray-600">
                                    <SortIcon className="mr-1 text-base" />
                                    Sorted by match score
                                </div>
                            </div>

                            {/* Results List */}
                            <div className="space-y-4">
                                {searchResults.map((parcel: ParcelResultDto) => {
                                    const isExpanded = expandedItems.has(parcel.referencia_catastral);
                                    const buildingDetails = parcel.buildings || [];
                                    const unitDetails = parcel.units || [];

                                    return (
                                        <div
                                            key={parcel.referencia_catastral}
                                            className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-shadow duration-300"
                                        >
                                            {/* Main Parcel Content */}
                                            <div onClick={(event) => handleItemClick(parcel.referencia_catastral, event)} className="p-6 cursor-pointer">
                                                {/* Parcel Header */}
                                                <div className="flex justify-between items-start mb-4">
                                                    <div className="flex items-center flex-1">
                                                        <ApartmentIcon className="text-gray-400 mr-2" />
                                                        <h3 className="font-bold text-gray-800">
                                                            {parcel.referencia_catastral} -{' '}
                                                            {parcel.ParcelMunicipio || parcel.UnitMunicipality || parcel.municipio}
                                                        </h3>
                                                        <span
                                                            className={`ml-3 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded-full ${
                                                                parcel.match_score >= 80
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : parcel.match_score >= 60
                                                                      ? 'bg-yellow-100 text-yellow-800'
                                                                      : 'bg-red-100 text-red-800'
                                                            }`}
                                                        >
                                                            {parcel.match_score}% match
                                                        </span>
                                                        {parcel.primary_building_type && (
                                                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                                {parcel.primary_building_type}
                                                            </span>
                                                        )}
                                                        {parcel.primary_use_type && (
                                                            <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                                                {parcel.primary_use_type}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div className="flex items-center gap-2 ml-4">
                                                        {/* View Details Button */}
                                                        <button
                                                            onClick={(event) => handleViewDetails(parcel.referencia_catastral, event)}
                                                            className="px-3 py-1 text-sm bg-my-blue text-white rounded-md hover:bg-opacity-90 transition-colors flex items-center gap-1"
                                                        >
                                                            <InfoIcon className="w-4 h-4" />
                                                            View Details
                                                        </button>

                                                        {/* Expand/Collapse Button */}
                                                        <button
                                                            onClick={(event) => toggleExpanded(parcel.referencia_catastral, event)}
                                                            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                                                        >
                                                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Basic Stats Grid */}
                                                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                                                    <div>
                                                        <p className="text-gray-500">Parcel Area</p>
                                                        <p className="font-medium text-gray-800">{formatNumber(parcel.superficie_oficial)} m²</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-500">Built Area</p>
                                                        <p className="font-medium text-gray-800">{formatNumber(parcel.total_built_area)} m²</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-500">Living Area</p>
                                                        <p className="font-medium text-gray-800">{formatNumber(parcel.total_floor_area || 0)} m²</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-500">Construction Year</p>
                                                        <p className="font-medium text-gray-800">{parcel.avg_year_built || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-500">Buildings</p>
                                                        <p className="font-medium text-gray-800">{parcel.num_buildings}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-500">Units</p>
                                                        <p className="font-medium text-gray-800">{parcel.num_units}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Expanded Content */}
                                            {isExpanded && (
                                                <div className="px-6 pb-6 border-t border-gray-100">
                                                    {/* Enhanced Metrics */}
                                                    <div className="mt-4">
                                                        <h4 className="text-lg font-semibold text-gray-800 mb-3">Enhanced Metrics</h4>
                                                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
                                                            {parcel.total_floor_area !== undefined && parcel.total_floor_area !== null && (
                                                                <div>
                                                                    <p className="text-gray-500">Total Living Area</p>
                                                                    <p className="font-medium text-gray-800">{formatNumber(parcel.total_floor_area)} m²</p>
                                                                </div>
                                                            )}

                                                            {parcel.oldest_year_built && (
                                                                <div>
                                                                    <p className="text-gray-500">Oldest Building</p>
                                                                    <p className="font-medium text-gray-800">{parcel.oldest_year_built}</p>
                                                                </div>
                                                            )}
                                                            {parcel.newest_year_built && (
                                                                <div>
                                                                    <p className="text-gray-500">Newest Building</p>
                                                                    <p className="font-medium text-gray-800">{parcel.newest_year_built}</p>
                                                                </div>
                                                            )}
                                                            {parcel.unique_building_types !== undefined && parcel.unique_building_types !== null && (
                                                                <div>
                                                                    <p className="text-gray-500">Building Types</p>
                                                                    <p className="font-medium text-gray-800">{parcel.unique_building_types}</p>
                                                                </div>
                                                            )}
                                                            {parcel.unique_use_types !== undefined && parcel.unique_use_types !== null && (
                                                                <div>
                                                                    <p className="text-gray-500">Use Types</p>
                                                                    <p className="font-medium text-gray-800">{parcel.unique_use_types}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Building Details */}
                                                    {buildingDetails.length > 0 && (
                                                        <div className="mt-6">
                                                            <h4 className="text-lg font-semibold text-gray-800 mb-3">Building Details</h4>
                                                            <div className="overflow-x-auto">
                                                                <table className="min-w-full divide-y divide-gray-200">
                                                                    <thead className="bg-gray-50">
                                                                        <tr>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                ID
                                                                            </th>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                Area (m²)
                                                                            </th>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                Type
                                                                            </th>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                Floor
                                                                            </th>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                Description
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                                        {buildingDetails.map((building, index) => (
                                                                            <tr key={index} className="hover:bg-gray-50">
                                                                                <td className="px-4 py-2 text-sm text-gray-900">{building.id}</td>
                                                                                <td className="px-4 py-2 text-sm text-gray-900">
                                                                                    {formatNumber(building.built_area)}
                                                                                </td>
                                                                                <td className="px-4 py-2 text-sm text-gray-900">{building.building_type}</td>
                                                                                <td className="px-4 py-2 text-sm text-gray-900">{building.floor}</td>
                                                                                <td className="px-4 py-2 text-sm text-gray-900">{building.description}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Unit Details */}
                                                    {unitDetails.length > 0 && (
                                                        <div className="mt-6">
                                                            <h4 className="text-lg font-semibold text-gray-800 mb-3">Unit Details</h4>
                                                            <div className="overflow-x-auto">
                                                                <table className="min-w-full divide-y divide-gray-200">
                                                                    <thead className="bg-gray-50">
                                                                        <tr>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                ID
                                                                            </th>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                Area (m²)
                                                                            </th>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                Use Type
                                                                            </th>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                Year Built
                                                                            </th>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                Age
                                                                            </th>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                Floor
                                                                            </th>
                                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                                                Participation
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                                        {unitDetails.map((unit, index) => (
                                                                            <tr key={index} className="hover:bg-gray-50">
                                                                                <td className="px-4 py-2 text-sm text-gray-900">{unit.id}</td>
                                                                                <td className="px-4 py-2 text-sm text-gray-900">
                                                                                    {formatNumber(unit.floor_area)}
                                                                                </td>
                                                                                <td className="px-4 py-2 text-sm text-gray-900">{unit.use_type}</td>
                                                                                <td className="px-4 py-2 text-sm text-gray-900">{unit.year_built}</td>
                                                                                <td className="px-4 py-2 text-sm text-gray-900">{unit.age} years</td>
                                                                                <td className="px-4 py-2 text-sm text-gray-900">{unit.floor}</td>
                                                                                <td className="px-4 py-2 text-sm text-gray-900">
                                                                                    {unit.participation.toFixed(4)}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Empty State */}
                            {searchResults.length === 0 && (
                                <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
                                    <SearchIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                    <h3 className="text-lg font-medium text-gray-800 mb-2">No parcels found</h3>
                                    <p className="text-gray-500">Try adjusting your search filters to find more results.</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default CatastroSearchPage;
